import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { Upload, X, Plus } from '@phosphor-icons/react';
import { colors, inputs, typography } from '../theme/tokens';
import { useAuth } from '../contexts/AuthContext';
import { getCloudinaryConfig } from '../config/cloudinary';
import { generateSignature } from '../utils/cloudinary';
import { supabase } from '../config/supabase';
import Button from '../components/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { TitleLarge } from '../components/Typography';
import { useToast } from '../contexts/ToastContext';
import { Ingredient, DBIngredient, Category, CookingUnit } from '../types/recipe';

interface RecipeFormData {
  name: string;
  description: string;
  prepTime: string;
  cookTime: string;
  isFree: boolean;
  imageUrl: string;
  portionSize: string;
  categories: string[];
}

const COOKING_UNITS: CookingUnit[] = [
  'pinch',
  'dash',
  'smidgen',
  'splash',
  'glug',
  'piece',
  'cup',
  'tbsp',
  'tsp',
  'oz',
  'lb',
  'g',
  'kg',
  'ml',
  'l',
  'qt',
  'gal',
  'dozen',
  'can',
  'jar',
  'bottle',
  'package',
  'bag',
  'box',
  'head',
  'clove',
  'slice',
  'whole',
  'half',
  'quarter',
  'eighth',
  'to taste',
  'as needed'
];

const PageContainer = styled.div`
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const DetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const IngredientsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const Header = styled.div`
  padding: 2rem;
  border-bottom: 1px solid ${colors.neutral.offWhite};
  background-color: ${colors.neutral.white};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const StepperContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const Step = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .step-number {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${props => 
      props.isCompleted 
        ? colors.brand.laurel
        : props.isActive 
          ? colors.neutral.white 
          : colors.neutral.offWhite};
    border: 2px solid ${props => 
      props.isCompleted || props.isActive 
        ? colors.brand.laurel
        : colors.neutral.lightCharcoal};
    color: ${props => 
      props.isCompleted 
        ? colors.neutral.white
        : props.isActive 
          ? colors.brand.laurel 
          : colors.neutral.darkCharcoal};
    font-family: ${typography.label.large.fontFamily};
    font-size: ${typography.label.large.fontSize};
  }

  .step-label {
    color: ${props => 
      props.isActive 
        ? colors.brand.laurel 
        : colors.neutral.darkCharcoal};
    font-family: ${typography.label.medium.fontFamily};
    font-size: ${typography.label.medium.fontSize};
  }
`;

const StepConnector = styled.div<{ isCompleted: boolean }>`
  height: 2px;
  width: 4rem;
  background-color: ${props => 
    props.isCompleted 
      ? colors.brand.laurel 
      : colors.neutral.lightCharcoal};
`;

const HeaderTitle = styled(TitleLarge)`
  color: ${colors.brand.laurel};
  margin-bottom: 0.5rem;
`;

const FormContainer = styled.div`
  padding: 1rem;
  background-color: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  box-sizing: border-box;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-family: ${typography.label.medium.fontFamily};
  font-size: ${typography.label.medium.fontSize};
  color: ${colors.neutral.darkCharcoal};
  margin-bottom: 0.5rem;
`;

const RequiredLabel = styled(Label)`
  &::after {
    content: '*';
    color: ${colors.reds.bright};
    margin-left: 0.25rem;
  }
`;

const Input = styled.input`
  background: ${inputs.default.background};
  border: ${inputs.default.border};
  color: ${inputs.default.color};
  padding: ${inputs.sizes.default.padding};
  font-size: ${inputs.sizes.default.fontSize};
  border-radius: ${inputs.sizes.default.borderRadius};
  width: 100%;
  font-family: ${typography.paragraph.medium.fontFamily};
  margin-bottom: 16px;

  &::placeholder {
    color: ${inputs.default.placeholder};
  }

  &:focus {
    outline: none;
    border-color: ${inputs.default.focusBorder};
  }

  &:disabled {
    background-color: ${inputs.default.disabledBackground};
    color: ${inputs.default.disabledColor};
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  background: ${inputs.default.background};
  border: ${inputs.default.border};
  color: ${inputs.default.color};
  padding: ${inputs.sizes.default.padding};
  font-size: ${inputs.sizes.default.fontSize};
  border-radius: ${inputs.sizes.default.borderRadius};
  width: 100%;
  min-height: 100px;
  font-family: ${typography.paragraph.medium.fontFamily};
  resize: vertical;
  margin-bottom: 16px;

  &::placeholder {
    color: ${inputs.default.placeholder};
  }

  &:focus {
    outline: none;
    border-color: ${inputs.default.focusBorder};
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  label {
    cursor: pointer;
  }

  input[type="checkbox"] {
    width: 1rem;
    height: 1rem;
    border: ${inputs.default.border};
    border-radius: 2px;
    cursor: pointer;
    background-color: ${colors.neutral.white};
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    position: relative;

    &:checked {
      background-color: ${colors.brand.laurel};
      border-color: ${colors.brand.laurel};

      &::after {
        content: '';
        position: absolute;
        left: 4px;
        top: 1px;
        width: 4px;
        height: 8px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }

    &:focus {
      outline: none;
      border-color: ${inputs.default.focusBorder};
    }
  }
`;

const ImageUploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px dashed ${colors.neutral.lightCharcoal};
  border-radius: 4px;
  background: none;
  color: ${colors.neutral.darkCharcoal};
  cursor: pointer;
  width: 100%;
  justify-content: center;

  &:hover {
    border-color: ${colors.brand.laurel};
    color: ${colors.brand.laurel};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ImagePreviewContainer = styled.div`
  margin-top: 1rem;
  width: 100%;
  max-width: 200px;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: ${inputs.default.border};
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ErrorMessage = styled.div`
  color: ${colors.reds.bright};
  font-size: ${typography.label.medium.fontSize};
  margin-top: 0.5rem;
`;

const IngredientsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const IngredientRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const IngredientInput = styled.div`
  flex: 2;
  position: relative;
`;

const AmountInput = styled.div`
  flex: 1;
  
  input[type="number"] {
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    -moz-appearance: textfield;
  }
`;

const UnitSelect = styled.div`
  flex: 1;
  position: relative;
`;

const IngredientList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${colors.neutral.white};
  border: ${inputs.default.border};
  border-radius: ${inputs.sizes.default.borderRadius};
  max-height: 200px;
  overflow-y: auto;
  z-index: 100;
  display: none;

  &.visible {
    display: block;
  }
`;

const UnitList = styled(IngredientList)`
  z-index: 101;
  max-height: 300px;
  width: 200px;
  right: auto;
`;

const IngredientListItem = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.neutral.offWhite};
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  color: ${colors.reds.bright};
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: ${colors.neutral.offWhite};
  }
`;

const AddIngredientButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  color: ${colors.brand.laurel};
  background: none;
  border: none;
  cursor: pointer;
  margin-top: 1rem;
  border-radius: 4px;

  &:hover {
    background-color: ${colors.neutral.offWhite};
  }
`;

const CategoryChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const CategoryChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${colors.brand.laurel};
  color: ${colors.neutral.white};
  padding: 12px;
  border-radius: 8px;
  font-family: ${typography.label.large.fontFamily};
  font-size: ${typography.label.large.fontSize};
  line-height: ${typography.label.large.lineHeight};
`;

const RemoveChipButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${colors.neutral.white};
  cursor: pointer;
  padding: 0;
  margin: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const NotificationBanner = styled.div`
  background: linear-gradient(135deg, ${colors.brand.laurel}15, ${colors.brand.laurel}25);
  border: 1px solid ${colors.brand.laurel}40;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${colors.brand.laurel};
  font-family: ${typography.label.medium.fontFamily};
  font-size: ${typography.label.medium.fontSize};
`;

const CreateRecipe: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const [formData, setFormData] = useState<RecipeFormData>({
    name: '',
    description: '',
    prepTime: '',
    cookTime: '',
    isFree: false,
    imageUrl: '',
    portionSize: '',
    categories: [],
  });
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [ingredients, setIngredients] = useState<Ingredient[]>([
    { id: '1', name: '', amount: '', unit: '' }
  ]);
  const [allIngredients, setAllIngredients] = useState<DBIngredient[]>([]);
  const [filteredIngredients, setFilteredIngredients] = useState<DBIngredient[]>([]);
  const [] = useState<boolean>(false);
  const [, setSearchTerm] = useState<string>('');
  const [] = useState<boolean>(false);
  const [filteredUnits, setFilteredUnits] = useState<CookingUnit[]>(COOKING_UNITS);
  const [, setUnitSearchTerm] = useState<string>('');
  const [showCategoryList, setShowCategoryList] = useState<boolean>(false);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [categorySearchTerm, setCategorySearchTerm] = useState<string>('');
  const [openIngredientDropdownId, setOpenIngredientDropdownId] = useState<string | null>(null);
  const [openUnitDropdownId, setOpenUnitDropdownId] = useState<string | null>(null);
  const ingredientsRef = useRef<HTMLDivElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const [, setRecipeId] = useState<string | null>(null);
  const [isAIGenerated, setIsAIGenerated] = useState<boolean>(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ingredientsRef.current && !ingredientsRef.current.contains(event.target as Node)) {
        setOpenIngredientDropdownId(null);
      }
      if (categoriesRef.current && !categoriesRef.current.contains(event.target as Node)) {
        setShowCategoryList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      // Don't fetch if user is not authenticated
      if (!user) {
        return;
      }

      try {
        const { data, error } = await supabase
          .from('Categories')
          .select('*')
          .order('Name');
        
        if (error) {
          return;
        }

        setAllCategories(data || []);
        setFilteredCategories(data || []);
      } catch (error) {
      }
    };

    fetchCategories();
  }, [user]);

  useEffect(() => {
    const fetchIngredients = async () => {
      if (!user) {
        return;
      }

      try {
        const { data, error } = await supabase
          .from('Ingredients')
          .select('*')
          .order('Name');
        
        if (error) {
          return;
        }

        setAllIngredients(data || []);
        setFilteredIngredients(data || []);
      } catch (error) {
      }
    };

    fetchIngredients();
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    setIsUploading(true);
    try {
      const timestamp = Math.round(new Date().getTime() / 1000);
      const signature = await generateSignature(timestamp);
      const cloudinaryConfig = getCloudinaryConfig();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', cloudinaryConfig.apiKey);
      formData.append('timestamp', timestamp.toString());
      formData.append('signature', signature);
      
      const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`;
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      setFormData(prev => ({
        ...prev,
        imageUrl: data.secure_url
      }));
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to upload image. Please try again.');
      setSelectedImage(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleIngredientChange = (id: string, field: keyof Ingredient, value: string) => {
    setIngredients(prev => prev.map(ing => 
      ing.id === id ? { ...ing, [field]: value } : ing
    ));
  };

  const handleIngredientSearch = (value: string, id: string) => {
    setSearchTerm(value);
    // Reset filtered ingredients to show all when empty
    if (!value.trim()) {
      setFilteredIngredients(allIngredients);
    } else {
      setFilteredIngredients(
        allIngredients.filter(ingredient =>
          ingredient.Name.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
    setOpenIngredientDropdownId(id);
  };

  const handleUnitSearch = (value: string, id: string) => {
    setUnitSearchTerm(value);
    if (!value.trim()) {
      setFilteredUnits(COOKING_UNITS);
    } else {
      setFilteredUnits(
        COOKING_UNITS.filter(unit => 
          unit.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
    setOpenUnitDropdownId(id);
  };

  const addIngredient = () => {
    setIngredients(prev => [...prev, {
      id: Date.now().toString(),
      name: '',
      amount: '',
      unit: ''
    }]);
  };

  const removeIngredient = (id: string) => {
    setIngredients(prev => prev.filter(ing => ing.id !== id));
  };

  const handleCategorySearch = (value: string) => {
    setCategorySearchTerm(value);
    setShowCategoryList(true);
    if (!value.trim()) {
      setFilteredCategories(allCategories);
    } else {
      setFilteredCategories(
        allCategories.filter(category =>
          category.Name.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
  };

  const handleCategorySelect = (category: Category) => {
    if (!formData.categories.includes(category.Name)) {
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, category.Name]
      }));
    }
    setCategorySearchTerm('');
  };

  const handleCategoryRemove = (categoryToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(category => category !== categoryToRemove)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      if (!isEditing && !formData.imageUrl) {
        throw new Error('Please upload a recipe image');
      }

      // Get the creator's ID from CreatorDetails using the auth user's ID
      const { data: creatorDetails, error: creatorError } = await supabase
        .from('CreatorDetails')
        .select('id')
        .eq('AuthID', user.id)
        .single();

      if (creatorError) {
        throw new Error('Failed to verify creator account');
      }

      if (!creatorDetails) {
        throw new Error('No creator account found for the current user');
      }

      const creatorId = creatorDetails.id;

      // Format the date as an ISO string
      const now = new Date().toISOString();

      const recipeData = {
        Name: formData.name,
        Description: formData.description,
        PrepTime: parseInt(formData.prepTime) || 0,
        CookTime: parseInt(formData.cookTime) || 0,
        Free: formData.isFree,
        ImageURL: formData.imageUrl || '',
        UserID: creatorId,
        DateCreated: isEditing ? undefined : now,
        PortionSize: parseInt(formData.portionSize) || 1,
        Category: formData.categories.join(', '),
        Publish: false
      };

      let recipeResult;
      if (isEditing) {
        const { data, error } = await supabase
          .from('Recipes')
          .update(recipeData)
          .eq('RecipeId', id)
          .select()
          .single();
        
        if (error) throw error;
        recipeResult = data;
      } else {
        const { data, error } = await supabase
          .from('Recipes')
          .insert([recipeData])
          .select()
          .single();

        if (error) throw error;
        recipeResult = data;
      }

      if (!recipeResult) {
        throw new Error('Failed to create/update recipe - no ID returned');
      }

      setRecipeId(recipeResult.RecipeId);

      // Now create/update the recipe ingredients
      const recipeIngredientsData = ingredients
        .filter(ingredient => ingredient.id && ingredient.amount && ingredient.unit)
        .map(ingredient => ({
          RecipeID: Number(recipeResult.RecipeId),
          IngredientID: parseInt(ingredient.id),
          Quantity: parseFloat(ingredient.amount) || 0,
          Unit: ingredient.unit
        }));

      if (recipeIngredientsData.length > 0) {
        if (isEditing) {
          // Delete existing ingredients and insert new ones
          const { error: deleteError } = await supabase
            .from('RecipeIngredients')
            .delete()
            .eq('RecipeID', id);

          if (deleteError) throw deleteError;
        }

        const { error: ingredientsError } = await supabase
          .from('RecipeIngredients')
          .insert(recipeIngredientsData);

        if (ingredientsError) {
          if (!isEditing) {
            // If ingredients fail to save and we're creating a new recipe, delete the recipe
            const { error: deleteError } = await supabase
              .from('Recipes')
              .delete()
              .eq('RecipeId', recipeResult.RecipeId);

            if (deleteError) {
              console.error('Failed to delete recipe after ingredient save failure:', deleteError);
            }
          }
          throw new Error(`Failed to save ingredients: ${ingredientsError.message}`);
        }
      }

      navigate(`/recipes/create/${recipeResult.RecipeId}/instructions`);
    } catch (error) {
      console.error('Detailed error:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to create/update recipe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchRecipe = async () => {
      if (!id) return;

      try {
        const { data, error } = await supabase
          .from('Recipes')
          .select('*')
          .eq('RecipeId', id)
          .single();

        if (error) throw error;

        if (data) {
          setFormData({
            name: data.Name,
            description: data.Description,
            prepTime: data.PrepTime.toString(),
            cookTime: data.CookTime.toString(),
            isFree: data.Free,
            imageUrl: data.ImageURL,
            portionSize: data.PortionSize.toString(),
            categories: data.Category ? data.Category.split(', ') : [],
          });
          setSelectedImage(data.ImageURL);

          // Check if recipe was AI-generated (created recently and has content)
          const recipeDate = new Date(data.DateCreated);
          const now = new Date();
          const timeDiff = now.getTime() - recipeDate.getTime();
          const minutesDiff = timeDiff / (1000 * 60);
          
          // If recipe was created in the last 10 minutes and has content, it's likely AI-generated
          if (minutesDiff <= 10 && (data.Name || data.Description)) {
            setIsAIGenerated(true);
          }

          // Fetch existing ingredients
          const { data: ingredientsData, error: ingredientsError } = await supabase
            .from('RecipeIngredients')
            .select('*')
            .eq('RecipeID', id);

          if (ingredientsError) throw ingredientsError;

          if (ingredientsData) {
            const { data: dbIngredientsData } = await supabase
              .from('Ingredients')
              .select('*')
              .in('id', ingredientsData.map(ri => ri.IngredientID));

            if (dbIngredientsData) {
              setIngredients(ingredientsData.map(ri => {
                const dbIngredient = dbIngredientsData.find(di => di.id === ri.IngredientID);
                return {
                  id: ri.IngredientID.toString(),
                  name: dbIngredient?.Name || 'Unknown Ingredient',
                  amount: ri.Quantity.toString(),
                  unit: ri.Unit
                };
              }));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching recipe:', error);
        showToast('Failed to load recipe for editing', 'error');
        navigate('/recipes');
      }
    };

    fetchRecipe();
  }, [id, navigate, showToast]);

  return (
    <PageContainer>
      <Form onSubmit={handleSubmit}>
        <ContentContainer>
          <Header>
            <HeaderContent>
              <HeaderTitle>Create Recipe</HeaderTitle>
              <StepperContainer>
                <Step isActive={true} isCompleted={false}>
                  <div className="step-number">1</div>
                  <div className="step-label">Recipe Details</div>
                </Step>
                <StepConnector isCompleted={false} />
                <Step isActive={false} isCompleted={false}>
                  <div className="step-number">2</div>
                  <div className="step-label">Instructions</div>
                </Step>
              </StepperContainer>
            </HeaderContent>
            <HeaderButtons>
              <Button 
                type="button" 
                variant="outline"
                onClick={() => navigate('/recipes')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || isUploading}
              >
                {isSubmitting ? 'Creating Recipe...' : 'Save recipe & Continue'}
              </Button>
            </HeaderButtons>
          </Header>
          
          {isAIGenerated && (
            <NotificationBanner style={{ gridColumn: '1 / -1' }}>
              🤖 This recipe was generated with AI assistance. Please review and adjust the details as needed.
            </NotificationBanner>
          )}
          
          <DetailsColumn>
            <FormContainer>
              <FormGroup>
                <Label htmlFor="name">Recipe Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="description">Description</Label>
                <TextArea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <FormGroup>
                  <Label htmlFor="prepTime">Prep Time (minutes)</Label>
                  <Input
                    type="number"
                    id="prepTime"
                    name="prepTime"
                    value={formData.prepTime}
                    onChange={handleChange}
                    min="0"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="cookTime">Cook Time (minutes)</Label>
                  <Input
                    type="number"
                    id="cookTime"
                    name="cookTime"
                    value={formData.cookTime}
                    onChange={handleChange}
                    min="0"
                    required
                  />
                </FormGroup>
              </div>

              <FormGroup>
                <Label htmlFor="portionSize">Portion Size</Label>
                <Input
                  type="number"
                  id="portionSize"
                  name="portionSize"
                  value={formData.portionSize}
                  onChange={handleChange}
                  min="1"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="category">Categories</Label>
                <div style={{ position: 'relative' }} ref={categoriesRef}>
                  <Input
                    type="text"
                    id="category"
                    name="category"
                    value={categorySearchTerm}
                    onChange={(e) => {
                      handleCategorySearch(e.target.value);
                    }}
                    onFocus={() => setShowCategoryList(true)}
                    placeholder="Search categories"
                  />
                  <IngredientList className={showCategoryList ? 'visible' : ''}>
                    {filteredCategories
                      .filter(category => !formData.categories.includes(category.Name))
                      .map((category) => (
                        <IngredientListItem
                          key={category.id}
                          onClick={() => handleCategorySelect(category)}
                        >
                          {category.Name}
                        </IngredientListItem>
                      ))}
                  </IngredientList>
                </div>
                {formData.categories.length > 0 && (
                  <CategoryChipsContainer>
                    {formData.categories.map((category, index) => (
                      <CategoryChip key={index}>
                        {category}
                        <RemoveChipButton
                          onClick={() => handleCategoryRemove(category)}
                        >
                          <X size={20} />
                        </RemoveChipButton>
                      </CategoryChip>
                    ))}
                  </CategoryChipsContainer>
                )}
              </FormGroup>

              <FormGroup>
                <RequiredLabel htmlFor="imageUrl">Recipe Image</RequiredLabel>
                <ImageUploadButton 
                  type="button" 
                  onClick={handleImageClick}
                  disabled={isUploading}
                >
                  <Upload size={20} />
                  {isUploading ? 'Uploading...' : isEditing ? 'Change Image' : 'Upload Image'}
                </ImageUploadButton>
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  required={!isEditing}
                />
                {uploadError && (
                  <ErrorMessage>{uploadError}</ErrorMessage>
                )}
                {selectedImage && (
                  <ImagePreviewContainer>
                    <ImagePreview src={selectedImage} alt="Recipe preview" />
                  </ImagePreviewContainer>
                )}
                {formData.imageUrl && (
                  <Input
                    type="text"
                    id="imageUrl"
                    name="imageUrl"
                    value={formData.imageUrl}
                    disabled
                  />
                )}
                {!formData.imageUrl && !isUploading && !isEditing && (
                  <ErrorMessage>Please upload a recipe image</ErrorMessage>
                )}
              </FormGroup>

              <FormGroup>
                <Checkbox>
                  <input
                    type="checkbox"
                    id="isFree"
                    name="isFree"
                    checked={formData.isFree}
                    onChange={handleChange}
                  />
                  <Label htmlFor="isFree" style={{ margin: 0 }}>Free Recipe</Label>
                </Checkbox>
              </FormGroup>

              {submitError && (
                <ErrorMessage>{submitError}</ErrorMessage>
              )}
            </FormContainer>
          </DetailsColumn>

          <IngredientsColumn>
            <FormContainer>
              <FormGroup>
                <Label>Ingredients</Label>
                <IngredientsContainer ref={ingredientsRef}>
                  {ingredients.map(ingredient => (
                    <IngredientRow key={ingredient.id}>
                      <IngredientInput>
                        <Input
                          type="text"
                          value={ingredient.name}
                          onChange={(e) => {
                            handleIngredientChange(ingredient.id, 'name', e.target.value);
                            handleIngredientSearch(e.target.value, ingredient.id);
                          }}
                          onFocus={() => {
                            setOpenIngredientDropdownId(ingredient.id);
                            setFilteredIngredients(allIngredients);
                          }}
                          placeholder="Search ingredient"
                        />
                        <IngredientList className={openIngredientDropdownId === ingredient.id ? 'visible' : ''}>
                          {filteredIngredients.map((item) => (
                            <IngredientListItem
                              key={item.id}
                              onClick={() => {
                                handleIngredientChange(ingredient.id, 'name', item.Name);
                                handleIngredientChange(ingredient.id, 'id', item.id.toString());
                                setOpenIngredientDropdownId(null);
                              }}
                            >
                              {item.Name}
                            </IngredientListItem>
                          ))}
                        </IngredientList>
                      </IngredientInput>
                      <AmountInput>
                        <Input
                          type="number"
                          value={ingredient.amount}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              handleIngredientChange(ingredient.id, 'amount', value);
                            }
                          }}
                          placeholder="Amount"
                          min="0"
                          step="0.1"
                        />
                      </AmountInput>
                      <UnitSelect>
                        <Input
                          type="text"
                          value={ingredient.unit}
                          onChange={(e) => {
                            handleIngredientChange(ingredient.id, 'unit', e.target.value);
                            handleUnitSearch(e.target.value, ingredient.id);
                          }}
                          onFocus={() => {
                            setOpenUnitDropdownId(ingredient.id);
                            setFilteredUnits(COOKING_UNITS);
                          }}
                          placeholder="Unit"
                        />
                        <UnitList className={openUnitDropdownId === ingredient.id ? 'visible' : ''}>
                          {filteredUnits.map((unit, index) => (
                            <IngredientListItem
                              key={index}
                              onClick={() => {
                                handleIngredientChange(ingredient.id, 'unit', unit);
                                setOpenUnitDropdownId(null);
                              }}
                            >
                              {unit}
                            </IngredientListItem>
                          ))}
                        </UnitList>
                      </UnitSelect>
                      <RemoveButton onClick={() => removeIngredient(ingredient.id)}>
                        <X size={20} />
                      </RemoveButton>
                    </IngredientRow>
                  ))}
                  <AddIngredientButton 
                    onClick={addIngredient}
                    type="button"
                  >
                    <Plus size={20} />
                    Add New Ingredient
                  </AddIngredientButton>
                </IngredientsContainer>
              </FormGroup>
            </FormContainer>
          </IngredientsColumn>
        </ContentContainer>
      </Form>
    </PageContainer>
  );
};

export default CreateRecipe; 